import React, { useMemo } from 'react';
import { startOfMonth } from 'date-fns';
import { BaseShiftCalendar, ShiftCalendarProvider } from '@/components/calendar-core';
import { ComposedShiftProvider, useShiftData } from '@/components/shifts/context/ComposedShiftProvider';
import { DayShift, Employee } from '@/types';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/core/utils';
import { useBusinesses } from '@/hooks/business/useBusinesses';

// Raw shift data from the employee query
interface RawShift {
  id: string;
  start_time: string;
  end_time: string;
  status?: string;
  notes?: string;
  title?: string;
  employee_id?: string;
}

interface EmployeeShiftCalendarViewProps {
  shifts: RawShift[];
  employee: Employee | null;
  businessId: string | null;
  loading?: boolean;
  selectedMonth?: Date;
  className?: string;
}

// Transform raw shifts to DayShift format
const transformShifts = (shifts: RawShift[], employeeId: string, employeeName: string): DayShift[] => {
  return shifts.map(shift => ({
    id: shift.id,
    startTime: new Date(shift.start_time),
    endTime: new Date(shift.end_time),
    employeeId: shift.employee_id || employeeId,
    employeeName: employeeName,
    storedStatus: shift.status as any, // Status from database
    title: shift.title,
  }));
};

// Inner component that uses the contexts
const EmployeeCalendarContent: React.FC<{
  shifts: DayShift[];
  employee: Employee;
  businessId: string;
  loading?: boolean;
  selectedMonth: Date;
  className?: string;
}> = ({
  shifts,
  employee,
  businessId,
  loading = false,
  selectedMonth,
  className,
}) => {
  // Get simpleDisplayHours from ShiftDataProvider for auto display hours
  const { simpleDisplayHours, loadingDisplayHours } = useShiftData();

  // Convert simpleDisplayHours range to Date objects for BaseShiftCalendar
  const { displayStartTime, displayEndTime } = useMemo(() => {
    const { start, end } = simpleDisplayHours.range;
    // Create Date objects from the hour values
    const startDate = new Date(1970, 0, 1, start, 0, 0);
    // Handle overnight (end > 24) by capping at 23:59
    const endHour = Math.min(end, 23);
    const endMinute = end >= 24 ? 59 : 0;
    const endDate = new Date(1970, 0, 1, endHour, endMinute, 0);
    return { displayStartTime: startDate, displayEndTime: endDate };
  }, [simpleDisplayHours.range]);

  // Fetch business details for opening hours
  const { businesses } = useBusinesses();
  const businessDetails = useMemo(() =>
    businesses?.find(b => b.id === businessId),
    [businesses, businessId]
  );
  const openingHours = businessDetails?.opening_hours || [];

  if (loading) {
    return (
      <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card flex flex-col', className)}>
      <ShiftCalendarProvider>
        <BaseShiftCalendar
          variant="full"
          currentDisplayMonth={selectedMonth}
          shifts={shifts}
          employees={[employee]}
          workingHours={[]}
          openingHours={openingHours}
          businessId={businessId}
          loading={loading}
          displayStartTime={displayStartTime}
          displayEndTime={displayEndTime}
          smartDisplayHours={simpleDisplayHours}
          enableShiftManagement={false}
          isEditingMode={false}
          hideStatusIndicators={true}
          hideControls={true}
          hideZoomControls={true}
          hideActionButtons={true}
          hideSidePanels={true}
          hideDayColumn={false}
          hideActionsColumn={true}
          expandAllDays={true}
          hidePastDays={false}
          fullHeight={false}
          loadingShifts={loading}
          loadingEmployees={false}
          loadingDisplayHours={loadingDisplayHours}
        />
      </ShiftCalendarProvider>
    </div>
  );
};

// Main component that sets up the providers
const EmployeeShiftCalendarView: React.FC<EmployeeShiftCalendarViewProps> = ({
  shifts,
  employee,
  businessId,
  loading = false,
  selectedMonth,
  className,
}) => {
  // Default to current month if not provided
  const displayMonth = useMemo(() =>
    selectedMonth || startOfMonth(new Date()),
    [selectedMonth]
  );

  // Transform shifts to DayShift format
  const transformedShifts = useMemo(() => {
    if (!employee?.id) return [];
    return transformShifts(shifts, employee.id, employee.name);
  }, [shifts, employee?.id, employee?.name]);

  if (!employee || !businessId) {
    return (
      <div className={cn('flex items-center justify-center h-[200px] text-muted-foreground text-sm', className)}>
        No employee selected
      </div>
    );
  }

  return (
    <ComposedShiftProvider businessId={businessId}>
      <EmployeeCalendarContent
        shifts={transformedShifts}
        employee={employee}
        businessId={businessId}
        loading={loading}
        selectedMonth={displayMonth}
        className={className}
      />
    </ComposedShiftProvider>
  );
};

export default EmployeeShiftCalendarView;
